import { type PlanAllowance, type PlanLimitNoticeUnit, planLimitHolds, topPlanAllowance } from './plan-limit-notice'

/**
 * The one billing flag the enterprise picker shows on an enterprise, so an owner of several sees at a glance which
 * need them on Billing without signing into each. It says what the enterprise's own banners would: the past-due
 * banner and the plan-limit banner, in their tones.
 * - `blocked` (the banners' magenta): the plan is past due, or a limit holds something back: it has paused work, or
 *   mail is held for a mailbox over its daily limit (#805).
 * - `warning` (amber): a limit is coming close.
 *
 * Concurrency never flags: full runs at once only make new runs wait for a slot, and a plan with one run at a time
 * is full whenever a run is in progress, so it would flag nearly every enterprise.
 */
export interface EnterpriseBillingAlert {
    enterpriseId: string
    tone: 'warning' | 'blocked'
    reason: 'past_due' | 'limit_reached' | 'limit_near'
    /** The allowance a limit reason is about; null for past due. */
    unit: PlanLimitNoticeUnit | null
}

/**
 * The enterprise's flag, or null when Billing has nothing to say. Past due comes first, then a limit that holds
 * something back, then one coming close: the allowance the plan-limit banner itself would pick, less concurrency.
 */
export function enterpriseBillingAlert(
    enterpriseId: string,
    pastDue: boolean,
    allowances: readonly PlanAllowance[],
): EnterpriseBillingAlert | null {
    if (pastDue) {
        return { enterpriseId, tone: 'blocked', reason: 'past_due', unit: null }
    }
    const top = topPlanAllowance(allowances.filter(allowance => allowance.unit !== 'concurrency'))
    if (!top) {
        return null
    }
    return planLimitHolds(top.unit, top.state)
        ? { enterpriseId, tone: 'blocked', reason: 'limit_reached', unit: top.unit }
        : { enterpriseId, tone: 'warning', reason: 'limit_near', unit: top.unit }
}

/** What each metered allowance looks like at its limit, and coming close to it. */
const LIMIT_WORDS: Record<PlanLimitNoticeUnit, { reached: string; near: string }> = {
    portal_run: {
        reached: "all this month's runs are used, so new runs are paused",
        near: "most of this month's runs are used",
    },
    runner_minute: {
        reached: "all this month's runner minutes are used, so workspace runs are paused",
        near: "most of this month's runner minutes are used",
    },
    storage: { reached: 'storage is full, so new runs are paused', near: 'storage is nearly full' },
    concurrency: { reached: 'new runs wait for a slot', near: 'nearly every run slot is in use' },
    mail_inbound: {
        reached: "mail is held for a mailbox over today's mail limit",
        near: "a mailbox has nearly reached today's mail limit",
    },
}

/** The flag's tooltip: what is wrong, in a few words. */
export function enterpriseBillingAlertLabel(alert: Pick<EnterpriseBillingAlert, 'reason' | 'unit'>): string {
    if (alert.reason === 'past_due' || alert.unit === null) {
        return 'Billing: the last payment did not go through'
    }
    const words = LIMIT_WORDS[alert.unit]
    return `Billing: ${alert.reason === 'limit_reached' ? words.reached : words.near}`
}
